import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Badge, Collapse, Empty, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { BankOutlined, HomeOutlined } from '@ant-design/icons'
import { IntegrationIcons } from '../integration-icons'
import { InviteButton, flattenOrganizations, inviteExceptionLabel, sortActiveFirst } from './shared'
import type { CompanyStructureViewProps, Employee, Office, Organization } from './types'

const { Text, Paragraph } = Typography

/**
 * V4 — Таблица по офису.
 * Максимально «админский» вид: org collapse → office → таблица сотрудников.
 * Статус одной колонкой; тег «Активен» не дублируем с opacity.
 * Неактивные офисы свёрнуты по умолчанию.
 */
export function CompanyStructureV4Table({ structure, onInvite }: CompanyStructureViewProps) {
  const orgs = useMemo(() => flattenOrganizations(structure), [structure])

  const items = orgs.map(org => ({
    key: `org-${org.id}`,
    label: (
      <Space size={8} wrap>
        <BankOutlined />
        <Link to={`/new/organization/${org.id}`} onClick={e => e.stopPropagation()}>
          <Text strong>{org.name}</Text>
        </Link>
        <Badge
          status={org.is_active ? 'success' : 'default'}
          text={org.is_active ? 'активна' : 'неактивна'}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {org.offices.length} офис. · {org.offices.reduce((n, o) => n + o.employees.length, 0)}{' '}
          сотр.
        </Text>
      </Space>
    ),
    children: <OrgTables org={org} onInvite={onInvite} />,
  }))

  if (!items.length) return <Empty description="Нет организаций" />

  return (
    <Collapse
      size="small"
      defaultActiveKey={orgs.filter(o => o.is_active).map(o => `org-${o.id}`)}
      items={items}
    />
  )
}

function OrgTables({
  org,
  onInvite,
}: {
  org: Organization
  onInvite: CompanyStructureViewProps['onInvite']
}) {
  const offices = sortActiveFirst(org.offices)
  const [openKeys, setOpenKeys] = useState<string[]>(
    offices.filter(o => o.is_active).map(o => `office-${o.id}`),
  )

  if (offices.length === 0) {
    return (
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Нет офисов. <Link to={`/office/${org.id}/new`}>Добавить офис</Link>
      </Paragraph>
    )
  }

  return (
    <Collapse
      size="small"
      ghost
      activeKey={openKeys}
      onChange={keys => setOpenKeys(Array.isArray(keys) ? keys : [keys])}
      items={offices.map(office => ({
        key: `office-${office.id}`,
        label: (
          <Space size={8} wrap>
            <HomeOutlined />
            <Link to={`/office/${office.id}`} onClick={e => e.stopPropagation()}>
              <Text>{office.name}</Text>
            </Link>
            <Badge
              status={office.is_active ? 'processing' : 'default'}
              text={`${office.employees.length} сотр.`}
            />
            <IntegrationIcons integrations={office.integrations} size={14} />
          </Space>
        ),
        extra:
          office.is_active && org.is_active ? (
            <span onClick={e => e.stopPropagation()}>
              <InviteButton org={org} office={office} onInvite={onInvite} />
            </span>
          ) : undefined,
        children: <EmployeesTable employees={sortActiveFirst(office.employees)} office={office} />,
      }))}
    />
  )
}

function EmployeesTable({ employees, office }: { employees: Employee[]; office: Office }) {
  if (employees.length === 0) {
    return <Text type="secondary">Сотрудников пока нет</Text>
  }

  const columns: ColumnsType<Employee> = [
    {
      title: 'Сотрудник',
      key: 'name',
      render: (_, emp) => (
        <Space direction="vertical" size={0}>
          <Link to={`/employees_new/${emp.id}`}>
            <Text strong={!emp.is_active ? false : true}>{emp.name || emp.login}</Text>
          </Link>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {emp.login}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Роли',
      key: 'roles',
      render: (_, emp) =>
        emp.roles.length ? (
          <Space size={[4, 4]} wrap>
            {emp.roles.map(r => (
              <Tag key={r.id} style={{ margin: 0 }}>
                {r.name}
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Статус',
      key: 'status',
      width: 160,
      render: (_, emp) => {
        const exception = inviteExceptionLabel(emp)
        if (exception) return exception
        return emp.is_active ? (
          <Badge status="success" text="Активен" />
        ) : (
          <Badge status="default" text="Неактивен" />
        )
      },
    },
    {
      title: 'Mango',
      key: 'integrations',
      width: 72,
      render: (_, emp) => (
        <IntegrationIcons integrations={emp.integrations} size={14} services={['mango']} />
      ),
    },
  ]

  return (
    <Table
      size="small"
      rowKey="id"
      pagination={employees.length > 8 ? { pageSize: 8, size: 'small' } : false}
      dataSource={employees}
      columns={columns}
      onRow={emp => ({
        style: {
          opacity:
            !office.is_active ||
            (!emp.is_active && emp.invite_status !== 'pending' && emp.invite_status !== 'expired')
              ? 0.45
              : 1,
        },
      })}
      style={{ marginTop: 4 }}
    />
  )
}
